import { Dialog } from '@mui/material';
import { getOwnBankAccounts } from 'lib/api/bankAccounts';
import { getCompanyConfigOld, triggerScrapOwnBusiness } from 'lib/api/company';
import { getCompanyFunds } from 'lib/api/funds';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import {
  createFactoringReSellBatch,
  getDocumentsAvailableForReSelling,
  getDocumentsPreparedForResell,
  updateFactoringReSellBatch,
} from 'lib/api/reSell/factoring';
import { getSelectedCertificate } from 'lib/api/SIICertificates';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import { calculateDays } from 'lib/functions/dates';
import { errorHandler } from 'lib/functions/errors';
import { calculateFundRateCost } from 'lib/functions/fundCostRateCalculation';
import {
  calculateResellSummary,
  formatDate,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import { PaginationInfo } from 'pages/MyAccounts/types/businessRelations';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { FactoringResellContext } from '../../context';
import MultiResellDocumentSelector from './components/addDocuments';
import BatchImporter from './components/BatchImporter';
import SimpleCessionDialog from './components/CessionDialog';
import CessionStatusManager from './components/CessionStatusManager';
import DeleteNomineeModal from './components/DeleteNomineeModal';
import ResellDialogActions from './components/DialogActions';
import ResellsTable from './components/ResellsTable';
import ResellSummary from './components/ResellSummary';
import {
  ApiResellResponse,
  BankAccount,
  CessionDocument,
  FundInfo,
  ResellDocument,
  ResellSuggestedDocument,
} from './types';

export interface MultiResellMainProps {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  setUp?: (params: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => Promise<unknown>;
  pagination?: PaginationInfo;
  resellNominees?: unknown[];
  // Props para vista de nominee
  initialResells?: ResellDocument[];
  initialAmountToApply?: number;
  initialFundId?: string;
  initialOperationDate?: Date;
  initialApplications?: ResellSuggestedDocument[];
  initialApplicationsIds?: number[];
  nomineeId?: string;
  readOnly?: boolean;
  setReadOnly?: (readOnly: boolean) => void;
  nominee?: {
    amountToReceive?: number;
  };
}

interface ValidationRule {
  condition: (resell: ResellDocument) => boolean;
  message: (folio: string) => string;
}

const MultiResellMain: React.FC<MultiResellMainProps> = (props) => {
  const {
    open: openProp,
    setOpen: setOpenProp,
    setUp: setUpProp,
    pagination: paginationProp,
    initialResells,
    initialAmountToApply,
    initialFundId,
    initialOperationDate,
    initialApplications,
    initialApplicationsIds,
    nomineeId,
    readOnly = false,
    setReadOnly,
    nominee,
  } = props;

  const dispatch = useDispatch();
  const contextValue = useContext(FactoringResellContext);
  const { data: funds } = getCompanyFunds(dispatch);

  const openMultiSales = contextValue?.openMultiSales ?? openProp ?? false;
  const setOpenMultiSales =
    contextValue?.setOpenMultiSales ?? setOpenProp ?? (() => {});
  const fetchResellingsFromContext = contextValue?.fetchResellings;
  const pagination = contextValue?.pagination ??
    paginationProp ?? { page: 0, pageSize: 10, total: 0, totalPages: 0 };
  const PAGE_SIZE = contextValue?.PAGE_SIZE ?? 10;

  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<ResellDocument[]>([]);
  const [documents, setDocuments] = useState<string[]>([]);
  const [fundId, setFundId] = useState<string>('');
  const [resells, setResells] = useState<ResellDocument[]>([]);
  const [openDocumentSelector, setOpenDocumentSelector] = useState(false);
  const [canForcePaymentDate, setCanForcePaymentDate] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [openCessionDialog, setOpenCessionDialog] = useState(false);
  const [hasCessionIntegration, setHasCessionIntegration] = useState(false);
  const [openDocumentBatchImporter, setOpenDocumentBatchImporter] =
    useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [modifiedResells, setModifiedResells] = useState<Set<string>>(
    new Set(),
  );
  const [amountToReceiveInput, setAmountToReceiveInput] = useState<number>(0);
  const [operationDate, setOperationDate] = useState<Date>(
    new Date(new Date().setUTCHours(0, 0, 0, 0)),
  );
  const [amountToApply, setAmountToApply] = useState(0);
  const [selectedResellsToApply, setSelectedResellsToApply] = useState<
    number[]
  >([]);
  const [resellsSuggested, setResellsSuggested] = useState<
    ResellSuggestedDocument[]
  >([]);
  const [resellsToApply, setResellsToApply] = useState<
    ResellSuggestedDocument[]
  >([]);

  const markResellAsModified = (resellId: string) => {
    setModifiedResells((prev) => new Set(prev).add(resellId));
  };

  const markAllResellsAsModified = () => {
    const allResellIds = resells.map((resell: ResellDocument) => resell.id);
    setModifiedResells(new Set(allResellIds));
  };

  const fundsCostDict = useMemo(() => {
    return (
      funds?.reduce((acc: Record<string, number>, fund: FundInfo) => {
        acc[fund.id] = fund.cost ?? 0;
        return acc;
      }, {}) ?? {}
    );
  }, [funds]);

  const fundCalculationMethodHash = useMemo(() => {
    return funds?.reduce(
      (
        acc: Record<string, string | undefined>,
        fund: FundInfo & {
          FundCompanyRelation?: { calculationMethod?: string }[];
        },
      ) => {
        const calculationMethod =
          fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

        if (fund.id) {
          acc[fund.id] = calculationMethod;
        }
        return acc;
      },
      {} as Record<string, string | undefined>,
    );
  }, [funds]);

  const recalculateRateCostForResells = (
    currentResells: ResellDocument[],
  ): ResellDocument[] => {
    return currentResells.map((resell) => {
      let days = resell.days || 0;
      if (resell.startDate && resell.endDate) {
        try {
          const calculatedDays = calculateDays(
            new Date(resell.startDate),
            new Date(resell.endDate),
          );
          days = calculatedDays > 0 ? calculatedDays : 0;
        } catch (error) {
          days = resell.days || 0;
        }
      }

      let rateCost = resell.rateCost || 0;
      let rate = resell.rate;

      if (resell.fundId && (rate === undefined || rate === null)) {
        rate = fundsCostDict[resell.fundId] || 0;
      }

      if (
        resell.fundId &&
        resell.amount &&
        resell.advance !== undefined &&
        resell.operationCostFixed !== undefined &&
        rate !== undefined &&
        rate >= 0 &&
        days >= 0
      ) {
        rateCost = calculateFundRateCost(
          resell.amount,
          parseFloat(String(resell.advance)),
          parseFloat(String(resell.operationCostFixed)),
          parseFloat(String(rate)),
          days,
          fundCalculationMethodHash ?? {},
          resell.fundId,
        );
      }

      return {
        ...resell,
        days,
        rate: rate !== undefined ? rate : resell.rate,
        rateCost,
      };
    });
  };
  // Update resells when initialResells changes (for nominee view)
  useEffect(() => {
    if (initialResells) {
      setResells(initialResells);
      setModifiedResells(new Set());

      if (
        initialResells.length > 0 &&
        initialResells[0].bankAccountReceiverId
      ) {
        setBankAccountId(initialResells[0].bankAccountReceiverId);
      }
    }
  }, [initialResells]);

  useEffect(() => {
    if (initialAmountToApply) {
      setAmountToApply(initialAmountToApply);
    }
  }, [initialAmountToApply]);

  useEffect(() => {
    if (initialFundId) {
      setFundId(initialFundId);
    }
  }, [initialFundId]);

  useEffect(() => {
    if (nominee) {
      if (nominee.amountToReceive) {
        setAmountToReceiveInput(nominee.amountToReceive);
      } else {
        const summary = calculateResellSummary(resells, amountToApply);
        const calculatedAmountToReceive = summary.amountToReceive;

        const initialValue =
          nominee.amountToReceive !== undefined &&
          nominee.amountToReceive !== null &&
          nominee.amountToReceive > 0
            ? nominee.amountToReceive
            : calculatedAmountToReceive;

        setAmountToReceiveInput(initialValue);
      }
    }
  }, [nominee, resells, amountToApply]);

  useEffect(() => {
    if (initialOperationDate) {
      setOperationDate(initialOperationDate);
    }
  }, [initialOperationDate]);

  useEffect(() => {
    if (initialApplications) {
      setResellsToApply(initialApplications);
    }
  }, [initialApplications]);

  useEffect(() => {
    if (initialApplicationsIds) {
      setSelectedResellsToApply(initialApplicationsIds);
    }
  }, [initialApplicationsIds]);

  const isRecalculatingRef = useRef<boolean>(false);

  useEffect(() => {
    if (resells.length === 0 || isRecalculatingRef.current) {
      return;
    }

    isRecalculatingRef.current = true;

    const recalculatedResells = recalculateRateCostForResells(resells);

    const hasChanges = recalculatedResells.some((recalculated, index) => {
      const original = resells[index];
      return (
        Math.abs((recalculated.rateCost || 0) - (original.rateCost || 0)) >
          0.01 ||
        recalculated.days !== original.days ||
        recalculated.rate !== original.rate
      );
    });

    if (hasChanges) {
      setResells(recalculatedResells);
    }

    isRecalculatingRef.current = false;
  }, [resells, funds, fundId, fundsCostDict, fundCalculationMethodHash]);

  const validParams = () => {
    const validations: ValidationRule[] = [
      {
        condition: (resell: ResellDocument) => !resell.startDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de inicio`,
      },
      {
        condition: (resell: ResellDocument) => !resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de término`,
      },
      {
        condition: (resell: ResellDocument) => !resell.cessionDate,
        message: (folio: string) =>
          `La factura con folio ${folio} no tiene una fecha de cesión`,
      },
      {
        condition: (resell: ResellDocument) =>
          resell.startDate === resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} tiene una fecha de inicio igual a la fecha de término`,
      },
      {
        condition: (resell: ResellDocument) =>
          resell.startDate > resell.endDate,
        message: (folio: string) =>
          `La factura con folio ${folio} tiene una fecha de inicio mayor a la fecha de término`,
      },
    ];

    for (const resell of resells) {
      if (resell.deleted) {
        continue;
      }
      for (const validation of validations) {
        if (validation.condition(resell)) {
          showAlert(dispatch, 'error', validation.message(resell.folio));
          return false;
        }
      }
    }
    if (!fundId) {
      showAlert(dispatch, 'error', 'Debes seleccionar un fondo');
      return false;
    }
    return true;
  };

  const handleOpenCessionDialog = () => {
    if (!validParams()) {
      return;
    }
    setOpenCessionDialog(true);
  };

  const addUniqueResells = (newResells: ResellDocument[]) => {
    const oldResells = [...resells];
    const existingIds = new Set(oldResells.map((resell) => resell.id));
    const uniqueNewResells = newResells.filter(
      (resell) => !existingIds.has(resell.id),
    );
    setResells([...oldResells, ...uniqueNewResells]);
  };

  const getDocumentsData = async (
    documentIds: string[],
    fundIdParam?: string,
    operationDateParam?: Date,
  ): Promise<ResellDocument[]> => {
    try {
      const response = await getDocumentsPreparedForResell(
        dispatch,
        documentIds,
      );
      const startDate = operationDateParam
        ? formatDate(operationDateParam)
        : formatDate(new Date());
      const newResells: ResellDocument[] = response.map(
        (resell: ApiResellResponse) => {
          const paymentDateFormatted = formatDate(new Date(resell.paymentDate));
          return {
            id: resell.documentId,
            folio: resell.folio,
            originalAmount: resell.originalAmount,
            advance: 100,
            arrearRate: 0,
            businessIdentifier: resell.businessIdentifier,
            requestId: resell.requestId,
            requestType: resell.requestType,
            amount: resell.amount,
            stakeholderIdentifier: resell.stakeholderIdentifier,
            businessName: resell.businessName,
            stakeholderName: resell.stakeholderName,
            paymentDate: paymentDateFormatted,
            cessionDate: resell.cessionDate
              ? formatDate(new Date(resell.cessionDate))
              : null,
            tempStartDate: startDate,
            startDate: startDate,
            subType: resell.subType,
            tempEndDate: paymentDateFormatted,
            endDate: paymentDateFormatted,
            days: calculateDays(
              new Date(startDate),
              new Date(paymentDateFormatted),
            ),
            operationCostFixed: 0,
            operationCostVariable: 0,
            rate: 0,
            rateCost: 0,
            observation: '',
            deleted: false,
            fundId: fundIdParam || null,
          };
        },
      );
      return newResells;
    } catch (error) {
      return [];
    }
  };

  const handleSearchDataInBatchImporter = async (
    documentsSelected: { id: string; dueDate?: string }[] = [],
  ) => {
    setLoading(true);
    setOpenDocumentSelector(false);
    try {
      const ids = documentsSelected.map((d) => d.id);
      const idToDueDate = documentsSelected.reduce<
        Record<string, string | undefined>
      >((acc, curr) => {
        acc[curr.id] = curr.dueDate;
        return acc;
      }, {});

      const newResells = await getDocumentsData(ids, fundId, operationDate);

      const adjustedResells = newResells.map((resell) => {
        const excelDueDate = idToDueDate[resell.id];
        if (excelDueDate) {
          const formattedEnd = formatDate(new Date(excelDueDate));
          const newDays = calculateDays(
            new Date(resell.startDate),
            new Date(formattedEnd),
          );
          return {
            ...resell,
            tempEndDate: formattedEnd,
            endDate: formattedEnd,
            days: newDays,
          } as ResellDocument;
        }
        return resell;
      });

      const fundsCostDictLocal = funds?.reduce(
        (acc: Record<string, number>, fund: FundInfo) => {
          acc[fund.id] = fund.cost ?? 0;
          return acc;
        },
        {},
      );
      const fundCalculationMethodHashLocal = funds?.reduce(
        (
          acc: Record<string, string | undefined>,
          fund: FundInfo & {
            FundCompanyRelation?: { calculationMethod?: string }[];
          },
        ) => {
          const calculationMethod =
            fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

          if (fund.id) {
            acc[fund.id] = calculationMethod;
          }
          return acc;
        },
        {} as Record<string, string | undefined>,
      );

      const calculatedResells = adjustedResells.map((resell) => {
        if (fundId && operationDate) {
          const rate = fundsCostDictLocal?.[fundId] || 0;
          const rateCost = calculateFundRateCost(
            resell.amount,
            parseFloat(String(resell.advance)),
            parseFloat(String(resell.operationCostFixed)),
            rate,
            resell.days || 0,
            fundCalculationMethodHashLocal ?? {},
            fundId,
          );
          return {
            ...resell,
            fundId: fundId,
            rate,
            rateCost,
          } as ResellDocument;
        }
        return resell;
      });

      addUniqueResells(calculatedResells);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSearchData = async () => {
    setLoading(true);
    setOpenDocumentSelector(false);
    try {
      const newResells: ResellDocument[] = await getDocumentsData(
        documents,
        fundId,
        operationDate,
      );

      const resellsWithRate = newResells.map((resell) => {
        if (fundId && fundsCostDict[fundId]) {
          return {
            ...resell,
            rate: fundsCostDict[fundId],
          };
        }
        return resell;
      });

      addUniqueResells(resellsWithRate);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  // Función para refrescar datos - usa context o props según disponibilidad
  const fetchResellings = async (params: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    if (fetchResellingsFromContext) {
      return fetchResellingsFromContext(params);
    } else if (setUpProp) {
      return setUpProp(params);
    }
  };

  const setUp = async (params: { page: number; pageSize: number }) => {
    await fetchResellings(params);
  };

  const handleSubmit = async (documentsIds: string[] = []) => {
    if (!validParams()) {
      return;
    }
    const documentsToResell = resells.filter((r) => !r.deleted);
    if (!documentsToResell.length) {
      showAlert(
        dispatch,
        'error',
        'No se registró la venta de facturas. Si es un error, te pedimos que la registres manualmente. ',
      );
      return;
    }

    setLoading(true);
    try {
      const resellsPayload = documentsToResell.map(
        (resell: ResellDocument) => ({
          documentId: resell.id,
          rate: Number(resell.rate),
          rateCost: Number(resell.rateCost),
          amount: Number(resell.amount),
          advance: Number(resell.advance),
          arrearRate: Number(resell.arrearRate),
          endDate: new Date(resell.endDate),
          fundId: fundId,
          operationCostFixed: Number(resell.operationCostFixed) || 0,
          operationCostVariable: Number(resell.operationCostVariable) || 0,
          startDate: new Date(resell.startDate),
          bankAccountReceiverId: bankAccountId,
          cessionByPruff: false,
        }),
      );
      const resellToApplyPayload = resellsToApply.map(
        (resell: ResellSuggestedDocument) => ({
          resellId: resell.id,
          amount: Number(resell.amountToConciliate || 0),
        }),
      );
      await createFactoringReSellBatch(dispatch, {
        resells: resellsPayload,
        amountToReceive: amountToReceiveInput,
        operationDate: operationDate,
        amountToApply: amountToApply,
        resellsToApply: resellToApplyPayload,
      });
      setResells([]);
      setResellsToApply([]);
      setResellsSuggested([]);
      setSelectedResellsToApply([]);
      setAmountToApply(0);
      showAlert(dispatch, 'success', 'Venta ejecutada correctamente');
      if (!documentsIds.length) {
        setOpenMultiSales(false);
      }
      await setUp({ page: pagination.page, pageSize: PAGE_SIZE });
    } catch (error: unknown) {
      showAlert(dispatch, 'error', 'Error al ejecutar la venta');
    }
    setLoading(false);
  };

  const handleUpdateNomineeResells = async () => {
    if (!validParams()) {
      return;
    }
    const documentsToUpdate = nomineeId
      ? resells.filter((resell: ResellDocument) =>
          modifiedResells.has(resell.id),
        )
      : resells;

    const amountChanged = amountToReceiveInput !== nominee?.amountToReceive;
    if (!documentsToUpdate.length && !amountChanged) {
      showAlert(dispatch, 'warning', 'No hay cambios para guardar.');
      return;
    }

    setLoading(true);
    try {
      const batchPayload = {
        nomineeId,
        amountToReceive: amountToReceiveInput,
        resells: documentsToUpdate.map((resell: ResellDocument) => ({
          id: resell.id,
          data: {
            rate: parseFloat(String(resell.rate)),
            rateCost: parseFloat(String(resell.rateCost)),
            amount: parseFloat(String(resell.amount)),
            advance: parseFloat(String(resell.advance)),
            arrearRate: parseFloat(String(resell.arrearRate)),
            dueDate: new Date(resell.endDate),
            fundId: fundId,
            operationCostFixed: Number(resell.operationCostFixed) || 0,
            operationCostVariable: Number(resell.operationCostVariable) || 0,
            startDate: new Date(resell.startDate),
            bankAccountReceiverId: bankAccountId,
            deleted: resell.deleted,
          },
        })),
      };

      await updateFactoringReSellBatch(dispatch, batchPayload);

      showAlert(
        dispatch,
        'success',
        nomineeId
          ? `${documentsToUpdate.length} ventas actualizadas correctamente`
          : 'Nómina creada correctamente',
      );
      setOpenMultiSales(false);
      setModifiedResells(new Set());

      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        await setUp({ page: pagination.page, pageSize: PAGE_SIZE });
      } catch (setUpError) {
        console.error('Error refreshing page data:', setUpError);
      }

      setReadOnly?.(true);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al actualizar la nómina');
      setReadOnly?.(true);
    } finally {
      setLoading(false);
    }
  };

  const setUpMainPage = async () => {
    await setUp({ page: pagination.page, pageSize: PAGE_SIZE });
  };

  const handleDeleteNominee = async () => {
    setOpenMultiSales(false);
    await setUpMainPage();
  };

  const setUpSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await getDocumentsAvailableForReSelling(dispatch);
      setSuggestions(response.documentsProcessed);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const setUpConfig = async () => {
    try {
      const response = await getCompanyConfigOld(
        dispatch,
        'system_operations_fund_forcePaymentDate',
      );
      if (response === '1') {
        setCanForcePaymentDate(true);
      }
      const bankAccountsResponse = await getOwnBankAccounts(dispatch);
      setBankAccounts(bankAccountsResponse);
    } catch (error) {
      console.error('Error fetching date restrictions config', error);
    }
  };

  const setUpCertificates = async () => {
    try {
      const response = await getSelectedCertificate(dispatch);
      setSelectedCertificate(response);
    } catch (error) {
      console.error(error);
    }
  };

  const checkCessionIntegration = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'recesion',
      );
      setHasCessionIntegration(response.status);
    } catch (error) {
      console.error(error);
    }
  };

  const setUpDialog = async () => {
    setLoading(true);
    await setUpConfig();
    await setUpCertificates();
    await checkCessionIntegration();
    setLoading(false);
  };

  useEffect(() => {
    void setUpDialog();
  }, []);

  useEffect(() => {
    if (!nomineeId && !readOnly) {
      void setUpSuggestions();
    }
  }, [nomineeId, readOnly]);

  const sendToScrapCession = async () => {
    try {
      void triggerScrapOwnBusiness(dispatch);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (hasCessionIntegration) {
      void sendToScrapCession();
    }
  }, [hasCessionIntegration]);

  const suggestionsAvailables = suggestions.filter(
    (suggestion: ResellDocument) =>
      !resells.some((resell: ResellDocument) => resell.id === suggestion.id),
  );

  const canDeleteNominee =
    nomineeId && validatePermissions(user, 'financing_fundSale_delete');

  return (
    <Dialog maxWidth="xl" fullWidth open={openMultiSales}>
      <div style={{ padding: 20, minHeight: 200 }}>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div>
            <HeaderWithActions
              title={nomineeId ? `Nómina #${nomineeId}` : 'Crear nómina '}
              type="dialogHeader"
            />
            <ResellSummary
              resells={resells}
              setResells={setResells}
              bankAccountId={bankAccountId}
              setBankAccountId={setBankAccountId}
              bankAccounts={bankAccounts}
              amountToReceiveInput={amountToReceiveInput}
              setAmountToReceiveInput={setAmountToReceiveInput}
              nomineeId={nomineeId}
              readOnly={readOnly}
              markAllResellsAsModified={markAllResellsAsModified}
              fundId={fundId}
              setFundId={setFundId}
              operationDate={operationDate}
              setOperationDate={setOperationDate}
              amountToApply={amountToApply}
              setAmountToApply={setAmountToApply}
              selectedResellsToApply={selectedResellsToApply}
              setSelectedResellsToApply={setSelectedResellsToApply}
              resellsSuggested={resellsSuggested}
              setResellsSuggested={setResellsSuggested}
              resellsToApply={resellsToApply}
              setResellsToApply={setResellsToApply}
              nominee={nominee}
            />
            <div style={{ padding: '10px 0px' }}>
              <ResellsTable
                resells={resells}
                setResells={setResells}
                canForcePaymentDate={canForcePaymentDate}
                readOnly={readOnly}
                hasCessionIntegration={hasCessionIntegration}
                markResellAsModified={markResellAsModified}
                isNomineeView={!!nomineeId}
                setOpenDocumentSelector={setOpenDocumentSelector}
                setOpenDocumentBatchImporter={setOpenDocumentBatchImporter}
                loadingSuggestions={loadingSuggestions}
              />
            </div>
            <CessionStatusManager
              resells={resells}
              setResells={setResells}
              readOnly={readOnly}
              nomineeId={nomineeId}
              hasCessionIntegration={hasCessionIntegration}
              fundId={fundId}
              bankAccountId={bankAccountId}
            />
            <ResellDialogActions
              readOnly={readOnly}
              nomineeId={nomineeId}
              resells={resells}
              bankAccountId={bankAccountId}
              selectedCertificate={selectedCertificate}
              hasCessionIntegration={hasCessionIntegration}
              onEdit={() => {
                setReadOnly?.(false);
              }}
              onClose={() => {
                setOpenMultiSales(false);
                if (readOnly) {
                  setResells([]);
                }
              }}
              onExitEdit={() => {
                setReadOnly?.(true);
              }}
              onSave={handleUpdateNomineeResells}
              onConfirm={() => handleSubmit()}
              onConfirmAndCede={handleOpenCessionDialog}
              onDelete={() => setOpenDeleteModal(true)}
              canDelete={canDeleteNominee}
            />
          </div>
        )}
      </div>
      {openCessionDialog && (
        <SimpleCessionDialog
          open={openCessionDialog}
          setOpen={setOpenCessionDialog}
          bankAccountId={Number(bankAccountId)}
          amountToReceiveInput={amountToReceiveInput}
          resellsToApply={resellsToApply}
          documents={resells.map(
            (resell: ResellDocument): CessionDocument => ({
              id: resell.id,
              businessName: resell.businessName,
              businessIdentifier: resell.businessIdentifier,
              folio: resell.folio,
              fundId: fundId,
              fundName: resell.fundName,
              amount: resell.amount,
              advance: resell.advance,
              arrearRate: resell.arrearRate,
              originalAmount: resell.originalAmount,
              stakeholderIdentifier: resell.stakeholderIdentifier,
              stakeholderName: resell.stakeholderName,
              paymentDate: resell.paymentDate,
              cessionDate: resell.cessionDate,
              startDate: resell.startDate,
              endDate: resell.endDate,
              rate: resell.rate,
              rateCost: resell.rateCost,
              operationCostFixed: resell.operationCostFixed,
              operationCostVariable: resell.operationCostVariable,
              subType: resell.subType ?? '33',
            }),
          )}
          funds={funds}
          setUpMainPage={setUpMainPage}
        />
      )}
      {openDocumentSelector && (
        <MultiResellDocumentSelector
          suggestions={suggestionsAvailables}
          documents={documents}
          setDocuments={setDocuments}
          handleConfirm={handleSearchData}
          open={openDocumentSelector}
          setOpen={setOpenDocumentSelector}
        />
      )}
      {openDocumentBatchImporter && (
        <BatchImporter
          open={openDocumentBatchImporter}
          setOpen={setOpenDocumentBatchImporter}
          suggestions={suggestionsAvailables}
          onDocumentsSelected={handleSearchDataInBatchImporter}
        />
      )}
      {openDeleteModal && nomineeId && (
        <DeleteNomineeModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          nomineeId={nomineeId}
          onDeleteSuccess={handleDeleteNominee}
        />
      )}
    </Dialog>
  );
};

export default MultiResellMain;
